import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

// Modal for choosing recurring event options
const RecurringStudentModal = ({ isOpen, onClose, onConfirm, recurringMode, setRecurringMode }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center"
      onClick={(e) => {
        // Close when clicking outside the modal
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <div className="mb-4 flex justify-between items-center">
          <h3 className="text-lg font-medium">Recurring Event Options</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <p className="mb-4 text-gray-600">
          This event is part of a recurring series. How would you like to add students?
        </p>
        
        <div className="space-y-2 mb-6">
          <div 
            className={`p-3 border rounded-md cursor-pointer ${
              recurringMode === 'single' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onClick={() => setRecurringMode('single')}
          >
            <div className="font-medium">This event only</div>
            <div className="text-sm text-gray-500">Add students to just this specific event</div>
          </div>
          
          <div 
            className={`p-3 border rounded-md cursor-pointer ${
              recurringMode === 'future' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onClick={() => setRecurringMode('future')}
          >
            <div className="font-medium">This and future events</div>
            <div className="text-sm text-gray-500">Add students to this event and all future occurrences in the series</div>
          </div>
          
          <div 
            className={`p-3 border rounded-md cursor-pointer ${
              recurringMode === 'all' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onClick={() => setRecurringMode('all')}
          >
            <div className="font-medium">All events in the series</div>
            <div className="text-sm text-gray-500">Add students to all events in this recurring series</div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(recurringMode)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// Use the existing LeadSearchInput component but adapt it for deals
const DealSearchInput = ({ value, onChange, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = React.useRef(null);
  const searchTimeout = React.useRef(null);
  const inputRef = React.useRef(null);

  // Load selected deal details on mount if value exists
  useEffect(() => {
    const fetchDealDetails = async () => {
      if (value) {
        try {
          const token = localStorage.getItem('token');
          const config = {
            headers: {
              'x-auth-token': token
            }
          };
          
          const res = await axios.get(`/api/deals/${value}`, config);
          setSelectedDeal({ 
            id: res.data.id, 
            title: res.data.title, 
            student_name: res.data.student_name,
            lead_name: res.data.lead?.name
          });
          setSearchTerm(res.data.title || '');
        } catch (err) {
          console.error('Error fetching deal details:', err);
          setSelectedDeal(null);
          setSearchTerm('');
        }
      } else {
        setSelectedDeal(null);
        setSearchTerm('');
      }
    };

    fetchDealDetails();
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Reset highlighted index when dropdown opens/closes or deal list changes
  useEffect(() => {
    if (showDropdown && deals.length > 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [showDropdown, deals]);

  // Debounced search function
  const searchDeals = async (query) => {
    if (!query || query.length < 2) {
      setDeals([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      // Clear any existing timeout
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      // Set a new timeout (300ms debounce)
      searchTimeout.current = setTimeout(async () => {
        const token = localStorage.getItem('token');
        const config = {
          headers: {
            'x-auth-token': token
          }
        };

        const res = await axios.get(`/api/deals?search=${encodeURIComponent(query)}&limit=10`, config);
        setDeals(res.data.deals || []);
        setShowDropdown(true);
        setLoading(false);
      }, 300);
    } catch (err) {
      console.error('Error searching deals:', err);
      setDeals([]);
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchTerm(query);
    searchDeals(query);
  };

  const handleSelectDeal = (deal) => {
    // Update UI state
    setSelectedDeal(deal);
    setSearchTerm(''); // Clear search instead of showing title
    setShowDropdown(false);
    
    // Only call onChange once to avoid duplicate callbacks
    if (deal && deal.id) {
      onChange(deal.id);
    }
  };

  const handleClearSelection = () => {
    setSelectedDeal(null);
    setSearchTerm('');
    onChange('');
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // If dropdown is not shown, don't handle keyboard navigation
    if (!showDropdown || loading || deals.length === 0) return;
    
    // Handle arrow up/down
    if (e.key === 'ArrowDown') {
      e.preventDefault(); // Prevent cursor from moving in input
      setHighlightedIndex(prev => (prev < deals.length - 1) ? prev + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); // Prevent cursor from moving in input
      setHighlightedIndex(prev => (prev > 0) ? prev - 1 : deals.length - 1);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault(); // Prevent form submission
      handleSelectDeal(deals[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };
  
  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
          placeholder={placeholder || "Search deals by title or student name"}
          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 bg-white rounded-md"
        />
        {selectedDeal && (
          <button
            type="button"
            onClick={handleClearSelection}
            className="absolute right-2 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
          {loading ? (
            <div className="p-2 text-center text-gray-500">
              <svg className="animate-spin h-5 w-5 mx-auto text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : deals.length === 0 ? (
            <div className="p-2 text-center text-gray-500">No deals found</div>
          ) : (
            <ul>
              {deals.map((deal, index) => (
                <li
                  key={deal.id}
                  data-index={index}
                  onClick={() => handleSelectDeal(deal)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`px-4 py-2 cursor-pointer ${highlightedIndex === index ? 'bg-blue-100' : 'hover:bg-gray-100'}`}
                >
                  <div className="font-medium">{deal.title}</div>
                  <div className="text-sm text-gray-500">
                    {deal.student_name ? `Student: ${deal.student_name}` : ''} 
                    {deal.lead_name ? ` (Lead: ${deal.lead_name})` : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

DealSearchInput.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

const ManageStudentsModal = ({ isOpen, onClose, eventId, onSave }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState('');
  const [maxStudents, setMaxStudents] = useState(10);
  const [enrollmentCount, setEnrollmentCount] = useState(0);
  const [error, setError] = useState('');
  const [eventDetails, setEventDetails] = useState(null);
  const [dealToAdd, setDealToAdd] = useState(null);
  const [dealToRemove, setDealToRemove] = useState(null);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showRemoveRecurringModal, setShowRemoveRecurringModal] = useState(false);
  const [recurringMode, setRecurringMode] = useState('single');
  const [removeRecurringMode, setRemoveRecurringMode] = useState('single');
  const [modalAction, setModalAction] = useState('add'); // 'add' or 'remove'

  useEffect(() => {
    if (isOpen && eventId) {
      fetchEventDetails();
      fetchStudents();
    }
  }, [isOpen, eventId]);
  
  // Handle escape key to close only this modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (isOpen && e.key === 'Escape') {
        // Prevent default and stop propagation 
        // to prevent the event from reaching the parent modal
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    
    // Using capture phase to catch the event before it reaches other listeners
    document.addEventListener('keydown', handleEscapeKey, true);
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isOpen, onClose]);

  const fetchEventDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/calendar/' + eventId, {
        headers: {
          'x-auth-token': token
        }
      });
      setEventDetails(response.data);
    } catch (error) {
      console.error('Error fetching event details:', error);
      setError('Failed to load event details');
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/calendar/' + eventId + '/students', {
        headers: {
          'x-auth-token': token
        }
      });
      
      setStudents(response.data.students || []);
      setMaxStudents(response.data.max_students || 10);
      setEnrollmentCount(response.data.current_count || 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load enrolled students');
      setLoading(false);
    }
  };

  const handleAddStudent = async (dealIdParam) => {
    // Use the parameter if provided, otherwise use state value
    // This helps avoid race conditions with state updates
    const dealId = dealIdParam || selectedDealId;
    
    if (!dealId) {
      setError('Please select a student to add');
      return;
    }

    if (enrollmentCount >= maxStudents) {
      setError(`Cannot add more students. Maximum capacity (${maxStudents}) reached.`);
      return;
    }

    // Check if this is a recurring event
    if (eventDetails && eventDetails.recurring_id) {
      // Store the dealId and show the recurring modal
      setDealToAdd(dealId);
      setRecurringMode('single'); // Reset to default
      setModalAction('add');
      setShowRecurringModal(true);
      return;
    }

    // If not recurring, proceed with adding the student
    await addStudentToEvent(dealId, 'single');
  };
  
  // Function to add student based on recurring mode
  const addStudentToEvent = async (dealId, mode) => {
    try {
      setAdding(true);
      setError('');
      const token = localStorage.getItem('token');
      
      // Log what we're sending to the API
      console.log('Adding student with deal_id:', dealId, 'Mode:', mode);
      
      // Create the request params
      const params = {};
      
      // Add recurring parameters if needed
      if (eventDetails && eventDetails.recurring_id && mode !== 'single') {
        params.recurring_id = eventDetails.recurring_id;
        params.add_mode = mode; // 'single', 'future', or 'all'
      }
      
      await axios.post(
        `/api/calendar/${eventId}/students${mode !== 'single' ? '?add_mode=' + mode : ''}`, 
        { 
          deal_id: dealId,
          ...(mode !== 'single' && eventDetails?.recurring_id ? { recurring_id: eventDetails.recurring_id } : {})
        },
        {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json'
          }
        }
      );

      // Refresh the student list
      fetchStudents();
      // Clear selectedDealId but keep the modal open
      setSelectedDealId('');
      setAdding(false);
      
      // Notify parent component of changes - without closing the modal
      if (onSave) {
        // Pass false to onSave to indicate "don't close the modal"
        onSave(false);
      }
    } catch (error) {
      console.error('Error adding student:', error);
      setError(error.response?.data?.message || 'Failed to add student');
      setAdding(false);
    }
  };

  const handleRemoveStudent = async (dealId) => {
    // Check if this is a recurring event
    if (eventDetails && eventDetails.recurring_id) {
      // Store the dealId and show the recurring modal for removal
      setDealToRemove(dealId);
      setRemoveRecurringMode('single'); // Reset to default
      setModalAction('remove');
      setShowRemoveRecurringModal(true);
      return;
    }

    // If not recurring, proceed with removing the student
    await removeStudentFromEvent(dealId, 'single');
  };
  
  // Function to remove student based on recurring mode
  const removeStudentFromEvent = async (dealId, mode) => {
    try {
      setRemoving(true);
      setError('');
      const token = localStorage.getItem('token');
      
      // Add mode parameter to the URL if needed
      const modeParam = mode !== 'single' ? `?remove_mode=${mode}` : '';
      
      await axios.delete(`/api/calendar/${eventId}/students/${dealId}${modeParam}`, {
        headers: {
          'x-auth-token': token
        }
      });

      // Refresh the student list
      fetchStudents();
      setRemoving(false);
      
      // Notify parent component of changes - with false to prevent closing
      if (onSave) onSave(false);
    } catch (error) {
      console.error('Error removing student:', error);
      setError(error.response?.data?.message || 'Failed to remove student');
      setRemoving(false);
    }
  };
  
  // Handle confirmation from add recurring modal
  const handleRecurringConfirm = (mode) => {
    setShowRecurringModal(false);
    if (dealToAdd) {
      addStudentToEvent(dealToAdd, mode);
      setDealToAdd(null);
    }
  };
  
  // Handle confirmation from remove recurring modal
  const handleRemoveRecurringConfirm = (mode) => {
    setShowRemoveRecurringModal(false);
    if (dealToRemove) {
      removeStudentFromEvent(dealToRemove, mode);
      setDealToRemove(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
      onClick={(e) => {
        // Close when clicking outside the modal
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium">Manage Students</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="px-6 py-4 flex-1 overflow-auto">
          {/* Event Information */}
          {eventDetails && (
            <div className="mb-6">
              <h4 className="font-medium">{eventDetails.title}</h4>
              <div className="text-sm text-gray-600">
                Date: {new Date(eventDetails.start_time).toLocaleDateString()} {new Date(eventDetails.start_time).toLocaleTimeString()} - {new Date(eventDetails.end_time).toLocaleTimeString()}
              </div>
              {eventDetails.course_name && (
                <div className="text-sm text-gray-600">
                  Course: {eventDetails.course_name} ({eventDetails.course_age_group}, {eventDetails.course_level})
                </div>
              )}
              {eventDetails.tutor_name && (
                <div className="text-sm text-gray-600">
                  Tutor: {eventDetails.tutor_name}
                </div>
              )}
            </div>
          )}

          {/* Enrollment Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Student Enrollment</span>
              <span>{enrollmentCount} / {maxStudents}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${enrollmentCount >= maxStudents ? 'bg-red-500' : 'bg-blue-500'}`}
                style={{ width: `${Math.min(100, (enrollmentCount / maxStudents) * 100)}%` }}
              ></div>
            </div>
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="mb-4 text-red-500 text-sm p-2 border border-red-200 bg-red-50 rounded">
              {error}
            </div>
          )}
          
          {/* Add Student Form */}
          <div className="mb-6">
            <h4 className="text-md font-medium mb-2">Add Student</h4>
            <div className="flex gap-2">
              <div className="flex-1">
                <DealSearchInput 
                  value={""}
                  onChange={(dealId) => {
                    if (dealId && enrollmentCount < maxStudents) {
                      // Set the dealId first, then call handleAddStudent in a callback
                      // to ensure the state is updated before handleAddStudent executes
                      setSelectedDealId(dealId);
                      setTimeout(() => {
                        handleAddStudent(dealId); // Pass dealId directly to avoid state timing issues
                      }, 0);
                    }
                  }}
                  placeholder="Search for a student to add..."
                />
              </div>
              {adding && (
                <div className="px-4 py-2 flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Adding...</span>
                </div>
              )}
            </div>
            {enrollmentCount >= maxStudents && (
              <div className="mt-2 text-sm text-red-600">
                Cannot add more students. Maximum capacity ({maxStudents}) reached.
              </div>
            )}
          </div>
          
          {/* Student List */}
          <div>
            <h4 className="text-md font-medium mb-2">Enrolled Students</h4>
            {loading ? (
              <div className="text-center py-4">
                <svg className="animate-spin h-6 w-6 mx-auto text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No students enrolled</div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="py-2 px-4 border-b text-left">Student</th>
                      <th className="py-2 px-4 border-b text-left">Deal</th>
                      <th className="py-2 px-4 border-b text-left">Value</th>
                      <th className="py-2 px-4 border-b text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(student => (
                      <tr key={student.id}>
                        <td className="py-2 px-4 border-b">
                          {student.student_name || student.lead_name || 'Unknown'}
                        </td>
                        <td className="py-2 px-4 border-b">{student.deal_title}</td>
                        <td className="py-2 px-4 border-b">
                          {student.value ? `${student.value} ${student.currency}` : '-'}
                        </td>
                        <td className="py-2 px-4 border-b text-right">
                          <button
                            onClick={() => handleRemoveStudent(student.deal_id)}
                            disabled={removing}
                            className="text-red-600 hover:text-red-800"
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
      
      {/* Recurring options modal for adding students */}
      <RecurringStudentModal 
        isOpen={showRecurringModal}
        onClose={() => {
          setShowRecurringModal(false);
          setDealToAdd(null);
        }}
        onConfirm={handleRecurringConfirm}
        recurringMode={recurringMode}
        setRecurringMode={setRecurringMode}
      />
      
      {/* Recurring options modal for removing students */}
      <RecurringStudentModal 
        isOpen={showRemoveRecurringModal}
        onClose={() => {
          setShowRemoveRecurringModal(false);
          setDealToRemove(null);
        }}
        onConfirm={handleRemoveRecurringConfirm}
        recurringMode={removeRecurringMode}
        setRecurringMode={setRemoveRecurringMode}
      />
    </div>
  );
};

ManageStudentsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSave: PropTypes.func
};

export default ManageStudentsModal;